'use client';

import { useEffect, useRef, useState } from 'react';
import {
  InteractionApiError,
  fetchInteractionStatus,
  isRetryableInteractionError,
  isTerminalInteractionError,
  reportInteractionFailure,
} from '@/app/utils/auth-interactions-api';
import {
  INTERACTION_TERMINAL_REASONS,
  type InteractionApprovedHandler,
  type InteractionTerminalHandler,
} from './types';
import { missingResumeState } from './terminal-state';

const INITIAL_POLL_DELAY_MS = 2_000;
const MAX_POLL_DELAY_MS = 8_000;
const MAX_TRANSIENT_FAILURES = 3;

export default function MagicLinkPendingState({
  interactionToken,
  email,
  onApproved,
  onTerminal,
  onSwitchToLogin,
}: {
  interactionToken: string;
  email?: string;
  onApproved: InteractionApprovedHandler;
  onTerminal: InteractionTerminalHandler;
  onSwitchToLogin: () => void;
}) {
  const [isCompleting, setIsCompleting] = useState(false);
  const [completionFailed, setCompletionFailed] = useState(false);
  const completedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | undefined;
    let delay = INITIAL_POLL_DELAY_MS;
    let transientFailures = 0;

    function schedulePoll() {
      timeoutId = window.setTimeout(poll, delay);
      delay = Math.min(delay + INITIAL_POLL_DELAY_MS, MAX_POLL_DELAY_MS);
    }

    async function poll() {
      try {
        const status = await fetchInteractionStatus(interactionToken);
        if (cancelled || completedRef.current) {
          return;
        }
        transientFailures = 0;

        if (status.status === 'pending') {
          schedulePoll();
          return;
        }

        if (status.status === 'expired') {
          onTerminal({
            title: 'This sign-in request has expired',
            message: 'Start over to continue signing in.',
            actionLabel: 'Start over',
            reason: INTERACTION_TERMINAL_REASONS.expired,
          });
          return;
        }

        if (status.resumeUrl) {
          completedRef.current = true;
          setIsCompleting(true);
          const completed = await onApproved(status.resumeUrl);
          if (!completed && !cancelled) {
            setCompletionFailed(true);
            setIsCompleting(false);
          }
          return;
        }

        onTerminal(missingResumeState());
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (error instanceof InteractionApiError) {
          if (isTerminalInteractionError(error)) {
            onTerminal(error);
            return;
          }
          if (isRetryableInteractionError(error) && transientFailures < MAX_TRANSIENT_FAILURES) {
            transientFailures += 1;
            schedulePoll();
            return;
          }
          if (isRetryableInteractionError(error)) {
            onTerminal({
              title: 'Authentication status is unavailable',
              message:
                'We could not confirm this sign-in after several attempts. Start over to continue.',
              actionLabel: 'Start over',
            });
            return;
          }
          onTerminal(error);
          return;
        }
        reportInteractionFailure(error, {
          source: 'magic-link-pending-state',
          endpoint: '/api/auth/interactions/:interaction_token',
          operation: 'poll',
        });
        onTerminal({
          title: 'Authentication status is unavailable',
          message: 'Please start over and try again.',
          actionLabel: 'Start over',
        });
      }
    }

    timeoutId = window.setTimeout(poll, INITIAL_POLL_DELAY_MS);

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [interactionToken, onApproved, onTerminal]);

  return (
    <div className="w-full max-w-lg">
      <div className="mb-6">
        <h2 className="h3 font-merriweather mb-2">
          {completionFailed
            ? 'Magic link approved'
            : isCompleting
              ? 'Finishing sign in'
              : 'Check your email'}
        </h2>
        <p className="text-sm text-slate-600">
          {completionFailed
            ? 'Your email link was approved, but finishing sign in failed. Use the retry button above.'
            : isCompleting
              ? 'Your email link was approved. We are finishing your sign in.'
              : `We sent a login link${email ? ` to ${email}` : ''}. Leave this window open after clicking it.`}
        </p>
      </div>

      {!isCompleting && !completionFailed && (
        <div>
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="btn text-zinc-800 border-zinc-300 hover:bg-zinc-100 w-full"
          >
            Use password instead
          </button>
        </div>
      )}
    </div>
  );
}
